import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  Text,
  Button,
  Badge,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Flex,
} from '@chakra-ui/react';
import { withErrorBoundary } from 'components/errorBoundary/ErrorBoundary';
import { stripeService } from 'services/stripeService';
import { useAuth } from 'contexts/AuthContext';
import { StripeSubscription, BackendSubscription } from 'types/stripe';
import { SubscriptionForm } from './SubscriptionForm';
import { SubscriptionPriceWithDiscount } from './SubscriptionPriceWithDiscount';
import { PaymentMethodModal } from './PaymentMethodModal';
import PaymentMethod from 'views/admin/main/account/billing/components/PaymentMethod';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';

interface SubscriptionManagementProps {
  onSubscriptionUpdate?: () => void;
}

// Type guard to check if subscription is a Stripe subscription
const isStripeSubscription = (
  subscription: any,
): subscription is StripeSubscription => {
  return subscription.plan !== undefined;
};

const SubscriptionManagementInner: React.FC<SubscriptionManagementProps> = ({
  onSubscriptionUpdate,
}) => {
  const { token } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isUpgradeOpen,
    onOpen: onUpgradeOpen,
    onClose: onUpgradeClose,
  } = useDisclosure();
  const {
    isOpen: isPaymentMethodOpen,
    onOpen: onPaymentMethodOpen,
    onClose: onPaymentMethodClose,
  } = useDisclosure();
  const [subscriptions, setSubscriptions] = useState<
    (StripeSubscription | BackendSubscription)[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<
    StripeSubscription | BackendSubscription | null
  >(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [isCanceling, setIsCanceling] = useState(false);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setIsLoading(true);
        const fetchedSubscriptions =
          await stripeService.getCustomerSubscriptions(token);
        setSubscriptions(fetchedSubscriptions);
        const primary = fetchedSubscriptions[0];
        if (primary) {
          FirebaseAnalyticsService.setUserContext({
            plan_tier: String(primary.tier || 'unknown'),
          });
        }
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
        setError('Failed to load subscriptions');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriptions();
  }, [token]);

  const fetchSubscriptions = async () => {
    try {
      setIsLoading(true);
      const fetchedSubscriptions = await stripeService.getCustomerSubscriptions(
        token,
      );
      setSubscriptions(fetchedSubscriptions);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setError('Failed to load subscriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedSubscription) return;

    try {
      setIsCanceling(true);

      // Handle both subscription types
      if (isStripeSubscription(selectedSubscription)) {
        FirebaseAnalyticsService.trackFeatureUsage(
          'Billing',
          'cancel_subscription_started',
          { subscription_id: selectedSubscription.id },
        );
        await stripeService.cancelSubscription(selectedSubscription.id, token);
        FirebaseAnalyticsService.trackFeatureUsage(
          'Billing',
          'cancel_subscription_success',
          { subscription_id: selectedSubscription.id },
        );
      } else {
        // For backend subscriptions, you might need a different endpoint
        // For now, we'll just show a message for free tier
        if (selectedSubscription.tier === 'free') {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Billing',
            'cancel_subscription_blocked',
            { tier: 'free' },
          );
          toast({
            title: 'Free',
            description: 'Free tier subscriptions cannot be canceled',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          onClose();
          return;
        }
      }

      toast({
        title: 'Subscription Canceled',
        description: 'Your subscription has been canceled successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
      fetchSubscriptions();
      onSubscriptionUpdate?.();
    } catch (error: any) {
      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'cancel_subscription_failed',
      );
      toast({
        title: 'Error',
        description:
          error.response?.data?.message || 'Failed to cancel subscription',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCanceling(false);
    }
  };

  const handleUpgradeSuccess = (_subscription: any) => {
    FirebaseAnalyticsService.trackFeatureUsage(
      'Billing',
      'upgrade_subscription_success',
    );
    toast({
      title: 'Subscription Upgraded',
      description: 'Your subscription has been successfully upgraded',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });

    onUpgradeClose();
    fetchSubscriptions();
    onSubscriptionUpdate?.();
  };

  const openCancelModal = (
    subscription: StripeSubscription | BackendSubscription,
  ) => {
    setSelectedSubscription(subscription);
    onOpen();
  };

  const openPaymentMethodModal = (
    subscription: StripeSubscription | BackendSubscription,
  ) => {
    setSelectedSubscription(subscription);
    if (isStripeSubscription(subscription)) {
      setSelectedPaymentMethod(subscription.payment_method);
    }
    onPaymentMethodOpen();
  };

  const handlePaymentMethodUpdate = () => {
    fetchSubscriptions();
    onSubscriptionUpdate?.();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'canceled':
        return 'red';
      case 'past_due':
        return 'orange';
      case 'trialing':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatBackendDate = (dateObj: {
    _seconds: number;
    _nanoseconds: number;
  } | undefined | null): string => {
    if (!dateObj || typeof dateObj._seconds !== 'number') {
      return 'N/A';
    }
    return new Date(dateObj._seconds * 1000).toLocaleDateString();
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(price / 100);
  };

  const getSubscriptionDisplayInfo = (
    subscription: StripeSubscription | BackendSubscription,
  ) => {
    if (isStripeSubscription(subscription)) {
      return {
        name: subscription.plan.name,
        price: formatPrice(subscription.plan.price, subscription.plan.currency),
        status: subscription.status,
        currentPeriodStart: formatDate(subscription.current_period_start),
        currentPeriodEnd: formatDate(subscription.current_period_end),
        cancelAtPeriodEnd: subscription.cancel_at_period_end,
        canCancel: subscription.status === 'active',
        canUpgrade: subscription.status === 'active',
      };
    } else {
      return {
        name:
          subscription.tier === 'free'
            ? 'Free Plan'
            : `${subscription.tier} Plan`,
        price: subscription.tier === 'free' ? 'Free' : 'Paid',
        status: 'active',
        currentPeriodStart: formatBackendDate(subscription.createdAt),
        currentPeriodEnd: formatBackendDate(subscription.createdAt),
        cancelAtPeriodEnd: false,
        canCancel: subscription.tier !== 'free',
        canUpgrade: subscription.tier === 'free',
      };
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={12}>
        <Spinner size="md" color="brand.400" />
        <Text mt={4}>Loading subscriptions...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertTitle>Error!</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text fontSize="lg" mb={4}>
          No subscriptions found
        </Text>
        <Button
          bg="brand.400"
          color="black"
          _hover={{ bg: 'brand.300' }}
          onClick={onUpgradeOpen}
          borderRadius="full"
        >
          Create Subscription
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box bg="white" borderRadius="30px" p={8} boxShadow="md">
        {subscriptions.map((subscription, index) => {
          const info = getSubscriptionDisplayInfo(subscription);
          return (
            <Box key={index}>
              <HStack justify="space-between" align="flex-start" mb={4}>
                <Box flex={1}>
                  <Flex justify="space-between" align="center">
                    <HStack align="center" mb={2}>
                      <Text fontSize="xl" fontWeight="bold">
                        {info.name}
                      </Text>
                      <Badge colorScheme={getStatusColor(info.status)}>
                        {info.status}
                      </Badge>
                    </HStack>
                    {isStripeSubscription(subscription) ? (
                      <SubscriptionPriceWithDiscount
                        priceCents={subscription.plan.price}
                        currency={subscription.plan.currency}
                        interval={subscription.plan.interval}
                        discount={subscription.discount}
                      />
                    ) : (
                      <Text
                        fontSize="lg"
                        fontWeight="semibold"
                        color="brand.500"
                      >
                        {info.price === 'Free' && 'Free'}
                        {info.price !== 'Free' && (
                          <>
                            {info.price}
                            <span style={{ fontSize: '12px' }}>/month</span>
                          </>
                        )}
                      </Text>
                    )}
                  </Flex>
                  {info.price !== 'Free' && (
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      <div>Current period:</div>
                      <div>
                        {info.currentPeriodStart} - {info.currentPeriodEnd}
                      </div>
                    </Text>
                  )}
                  {info.cancelAtPeriodEnd && (
                    <Text fontSize="sm" color="orange.500" mt={1}>
                      Will cancel at period end
                    </Text>
                  )}
                </Box>
              </HStack>

              <PaymentMethod
                paymentMethod={
                  isStripeSubscription(subscription)
                    ? subscription.payment_method
                    : undefined
                }
                onEditClick={() => openPaymentMethodModal(subscription)}
              />

              <HStack justify="flex-end" spacing={3} mt={4}>
                {info.canCancel && (
                  <Button
                    variant="outline"
                    borderRadius="full"
                    onClick={() => openCancelModal(subscription)}
                  >
                    Cancel Subscription
                  </Button>
                )}

                <Button
                  disabled={!info.canUpgrade}
                  bg="brand.400"
                  _hover={{ bg: 'brand.300' }}
                  color="black"
                  onClick={onUpgradeOpen}
                  borderRadius="full"
                >
                  Upgrade
                </Button>
              </HStack>
            </Box>
          );
        })}
      </Box>

      {/* Cancel Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cancel Subscription</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to cancel your subscription? This action
              cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Keep Subscription
            </Button>
            <Button
              colorScheme="red"
              onClick={handleCancelSubscription}
              isLoading={isCanceling}
            >
              Cancel Subscription
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal used to upgrade subscription */}
      <Modal isOpen={isUpgradeOpen} onClose={onUpgradeClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upgrade Subscription</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SubscriptionForm onSuccess={handleUpgradeSuccess} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Model used to add/edit payment method for later use */}
      <PaymentMethodModal
        isOpen={isPaymentMethodOpen}
        onClose={onPaymentMethodClose}
        subscriptionId={
          selectedSubscription && isStripeSubscription(selectedSubscription)
            ? selectedSubscription.id
            : selectedSubscription &&
              !isStripeSubscription(selectedSubscription)
            ? selectedSubscription.stripeSubscriptionId || undefined
            : undefined
        }
        currentPaymentMethod={selectedPaymentMethod}
        onPaymentMethodUpdate={handlePaymentMethodUpdate}
      />
    </>
  );
};

export const SubscriptionManagement = withErrorBoundary(SubscriptionManagementInner, {
  title: 'Subscription Error',
  description: 'There was an error loading your subscription information. Please try again.',
});
